import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {FormikProvider, useFormik} from 'formik';
import {debounce} from 'debounce';
import {FormattedMessage, useIntl} from 'react-intl';
import {format, parseISO} from 'date-fns';
import {createColumnHelper, getCoreRowModel, Table, useReactTable} from '@tanstack/react-table';
import Spinner from '../../../../common/components/ui/Spinner';
import ButtonDetails from '../ButtonDetails';
import ButtonLink from '../ButtonLink';
import {InfiniteScrollProps} from '../../../../common/types';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {InvoiceOrderFilterValues, InvoiceOrderItem} from '../../types';
import {formatFloatThousandsWithCommas} from '../../../../common/utils';
import {ExcelExportService} from '../../../../services';

export type InvoiceOrderRowExtended = InvoiceOrderItem & {actions: unknown};

export type InvoiceOrdersGridContext = {
  invoiceOrdersList: InvoiceOrderRowExtended[],
  totalCount: number,
  totalAmount: number,
  infiniteScroll: {
    containerID: string,
    props: InfiniteScrollProps
  },
  table: Table<InvoiceOrderRowExtended>,
  saveListToExcelFile: () => void
}

export type InvoiceOrdersGridContextProviderProps = {
  children: React.ReactNode,
}

export const defaultContext: InvoiceOrdersGridContext = {
  invoiceOrdersList: [],
  totalCount: 0,
  totalAmount: 0,
  infiniteScroll: {
    containerID: 'scrollContainer',
    props: {
      next: () => {
      },
      hasMore: false,
      loader: null,
      dataLength: 0
    }
  },
  table: {} as Table<InvoiceOrderRowExtended>,
  saveListToExcelFile: () => {}
};

export const defaultFilters: InvoiceOrderFilterValues = {
  fromDate: null,
  toDate: null,
  text: ''
};

export const InvoiceOrdersGridContext = createContext<InvoiceOrdersGridContext>(defaultContext);
InvoiceOrdersGridContext.displayName = 'InvoiceOrdersGridContext';

export const useInvoiceOrdersGridContext = () => useContext(InvoiceOrdersGridContext);

export const InvoiceOrdersGridContextProvider = ({children}: InvoiceOrdersGridContextProviderProps) => {
  const intl = useIntl();
  const M = useMessage();
  const {apiService, DEBOUNCE_DELAY_MS} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const [invoiceOrdersList, setInvoiceOrdersList] = useState<InvoiceOrderRowExtended[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const formik = useFormik<InvoiceOrderFilterValues>({
    initialValues: {...defaultFilters},
    onSubmit: () => {},
    enableReinitialize: true,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const updateList = async (
    page: number,
    fromDate: Date | null,
    toDate: Date | null,
    text: string,
    previousList: InvoiceOrderRowExtended[]
  ) => {
    try {
      const result = await apiService.getInvoiceOrdersList(page, fromDate, toDate, text);

      if (result.errorCode < 0) {
        globalMessage.open(M.serverError);
      } else {
        setTotalAmount(result.body.totalAmount);
        setTotalCount(result.body.count);
        setInvoiceOrdersList([...previousList, ...result.body.list] as InvoiceOrderRowExtended[]);
      }
    } catch (e) {
      console.error(e);
      globalMessage.open(M.serverError);
    }
  };
  const callDebouncedHandler = debounce(updateList, DEBOUNCE_DELAY_MS, false);
  const update = useCallback(callDebouncedHandler, []);

  useEffect(() => {
    setPage(1);
    setLoading(true);
    updateList(1, null, null, '', [])
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!loading) {
      setPage(1);
      update(1, formik.values.fromDate, formik.values.toDate, formik.values.text, []);
    }
  }, [formik.values.text, formik.values.toDate, formik.values.fromDate]);


  const columnHelper = createColumnHelper<InvoiceOrderRowExtended>();
  const columns = [
    columnHelper.accessor('createdAt', {
      cell: info => {
        const value = info.getValue() as any;

        let date: Date | null = null;

        if (!value) return '-';

        if (typeof value === 'string') {
          date = parseISO(value);
        } else if (value instanceof Date) {
          date = value;
        } else if (typeof value === 'object' && value?.$date) {
          date = new Date(value?.$date);
        }

        if (!date || isNaN(date.getTime())) return '-';

        return format(date, 'dd/MM/yyyy');
      },
      header: () => <FormattedMessage id="invoiceOrders-col-createdAt" defaultMessage="Created At"/>,
    }),
    columnHelper.accessor('customerName', {
      cell: info => info.getValue(),
      header: () => <FormattedMessage id="invoiceOrders-col-customer" defaultMessage="Customer"/>,
    }),
    columnHelper.accessor('contact', {
      cell: info => info.getValue(),
      header: () => <FormattedMessage id="invoiceOrders-col-contact" defaultMessage="Contact"/>,
    }),
    columnHelper.accessor('phone', {
      cell: info => info.getValue(),
      header: () => <FormattedMessage id="invoiceOrders-col-phone" defaultMessage="Phone"/>,
    }),
    columnHelper.accessor('totalAmountVAT', {
      cell: info => Boolean(info) ? formatFloatThousandsWithCommas(info.getValue().toFixed(2)) : '',
      header: () => <FormattedMessage id="invoiceOrders-col-amount" defaultMessage="Amount"/>,
    }),
    columnHelper.accessor('actions', {
      cell: info => {
        return (
          <>
            <ButtonDetails row={info.cell.row.original}/>
            <ButtonLink id={info.cell.row.original._id}/>
          </>
        );
      },
      header: () => ''
    }),
  ];

  const table = useReactTable({
    data: invoiceOrdersList,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const next = () => {
    const nextPage = page + 1;
    updateList(nextPage, formik.values.fromDate, formik.values.toDate, formik.values.text, invoiceOrdersList)
      .then(() => setPage(nextPage))
      .catch(console.error);
  };
  const infiniteScrollProps: InfiniteScrollProps = {
    hasMore: invoiceOrdersList.length < totalCount,
    loader: <Spinner/>,
    dataLength: invoiceOrdersList.length,
    next, // should return any, not Promise<any>
    scrollThreshold: 0.9,
  };

  const saveListToExcelFile = async () => {
    try {
      const createdAt = intl.formatMessage({id: 'invoiceOrders-col-createdAt', defaultMessage: 'Create At'});
      const customerName = intl.formatMessage({id: 'invoiceOrders-col-customer', defaultMessage: 'Customer'});
      const contact = intl.formatMessage({id: 'invoiceOrders-col-contact', defaultMessage: 'Contact'});
      const phone = intl.formatMessage({id: 'invoiceOrders-col-phone', defaultMessage: 'Phone'});
      const totalAmountVAT = intl.formatMessage({id: 'invoiceOrders-col-amount', defaultMessage: 'Amount'});
      const {body: {list}} = await apiService.getInvoiceOrdersListForExcel(
        formik.values.fromDate,
        formik.values.toDate,
        formik.values.text
      );
      const data = [
        ...list.map(item => {
          return {
            [createdAt]: format(parseISO(`${item.createdAt}`), 'dd/MM/yyyy'),
            [customerName]: item.customerName,
            [contact]: item.contact,
            [phone]: item.phone,
            [totalAmountVAT]: Boolean(item.totalAmountVAT)
              ? formatFloatThousandsWithCommas(parseFloat(`${item.totalAmountVAT}`).toFixed(2))
              : '',
          };
        })
      ];
      const excelExport = new ExcelExportService();
      excelExport
        .addPage(data, '')
        .generateFile('חשבוניות מוזמנות.xlsx');

    } catch (e) {
      globalMessage.open(M.serverError);
    }
  };

  const context: InvoiceOrdersGridContext = {
    ...defaultContext,
    invoiceOrdersList,
    totalCount,
    totalAmount,
    table,
    infiniteScroll: {
      containerID: defaultContext.infiniteScroll.containerID,
      props: infiniteScrollProps
    },
    saveListToExcelFile
  };

  return loading
    ? <Spinner/>
    : (
      <InvoiceOrdersGridContext.Provider value={context}>
        <FormikProvider value={formik}>
          {children}
        </FormikProvider>
      </InvoiceOrdersGridContext.Provider>
    );
};
